import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Logout as LogoutIcon, Person as PersonIcon, AccountCircle as ProfileIcon, PersonAdd as AddLecturerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const UserMenu = () => {
  const { user, logout, isAdmin } = useAuth();
  const { t, isArabic } = useLanguage();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleClose();
  };

  const handleAddLecturerClick = () => {
    navigate('/add-lecturer');
    handleClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) return null;

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ ml: isArabic ? 0 : 1, mr: isArabic ? 1 : 0 }}
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar
          src={user.photoURL}
          alt={user.displayName}
          sx={{ width: 32, height: 32 }}
        >
          {user.displayName?.charAt(0) || <PersonIcon />}
        </Avatar>
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            direction: isArabic ? 'rtl' : 'ltr',
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: isArabic ? 'auto' : 14,
              left: isArabic ? 14 : 'auto',
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: isArabic ? 'left' : 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: isArabic ? 'left' : 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleProfileClick} sx={{ minWidth: 200, py: 1.5 }}>
          <Avatar src={user.photoURL} sx={{ mr: isArabic ? 0 : 2, ml: isArabic ? 2 : 0 }}>
            {user.displayName?.charAt(0) || <PersonIcon />}
          </Avatar>
          <div>
            <Typography variant="subtitle2" noWrap>
              {user.displayName || t('User', 'المستخدم')}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user.email}
            </Typography>
          </div>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleProfileClick} sx={{ py: 1 }}>
          <ListItemIcon>
            <ProfileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t('My Profile', 'ملفي الشخصي')}
          </ListItemText>
        </MenuItem>
        
        {/* Admin-only options */}
        {isAdmin && (
          <>
            <Divider />
            
            <MenuItem onClick={handleAddLecturerClick} sx={{ py: 1 }}>
              <ListItemIcon>
                <AddLecturerIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {t('Add Lecturer', 'إضافة محاضر')}
              </ListItemText>
            </MenuItem>
          </>
        )}
        
        <Divider />
        
        <MenuItem onClick={handleLogout} sx={{ py: 1 }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t('Sign Out', 'تسجيل الخروج')}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu; 